import type {
  FormFieldOptionItem,
  FormFieldType,
} from "@/server/platform/db/schema/forms";
import {
  parseRepeaterSettings,
  formatRepeaterAnswerDisplay,
} from "@/modules/forms/domain/lib/form-repeater-settings";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";

function fileBasename(fileKey: string): string {
  const i = fileKey.lastIndexOf("/");
  return i >= 0 ? fileKey.slice(i + 1) : fileKey;
}

/** ຂໍ້ມູນຟິວທີ່ຕ້ອງນໍາໄປເລືອກສະແດງ label ສຳລັບ choice / dropdown / checkbox / repeater */
export type FormAnswerDisplayFieldPick = {
  type: FormFieldType;
  options: FormFieldOptionItem[] | null;
  settings?: Record<string, unknown> | null;
};

function resolveOptionLabel(
  storedValue: string,
  options: FormFieldOptionItem[] | null,
): string {
  if (!options?.length) return storedValue;
  const hit = options.find((o) => o.value === storedValue);
  return hit?.label ?? storedValue;
}

/** ສະແດງຄ່າຕອບຄຸກຄ້າຍ ຕາຕະລາງ / CSV / ພິມ — ຄັດຟາຍເປັນຊື່ໄຟລ໌; ຕົວເລືອກໃຊ້ label ຈາກ options */
export function formatFormAnswerDisplay(
  cell: { value: string | null; fileKey: string | null } | undefined,
  field: FormAnswerDisplayFieldPick,
): string {
  if (!cell) return "—";
  if (cell.fileKey) return fileBasename(cell.fileKey);
  const v = cell.value;
  if (v == null || v === "") return "—";

  const { type, options, settings } = field;

  if (type === "repeater") {
    return formatRepeaterAnswerDisplay(v, parseRepeaterSettings(settings));
  }

  if (type === "checkbox") {
    try {
      const parsed: unknown = JSON.parse(v);
      if (Array.isArray(parsed)) {
        const labels = parsed.map((x) =>
          resolveOptionLabel(String(x), options),
        );
        if (labels.length === 0) return "—";
        return labels.join(", ");
      }
    } catch {
      /* skip */
    }
    return v;
  }

  if (type === "choice" || type === "dropdown") {
    return resolveOptionLabel(v, options);
  }

  if (type === "datetime") {
    return formatDateTime(v);
  }

  if (type === "date") {
    return formatDate(v);
  }

  return v;
}
